/**
 * The player buzzer (presentational + one action). A single big button that sends
 * the `buzz` command (FR-4.1); it is enabled only when buzzing is currently legal
 * for this player (window open, not locked out, not already holding it). Its label
 * reflects the authoritative state so the player always knows why they can't buzz.
 *
 * `onEarlyPress` (#198, FR-4.11 — buzzer-smash lockout): a `disabled` button swallows
 * pointer/click events entirely, so an early "smash" during the pre-open "Wait…"
 * window never reaches any handler. To detect smashing purely on the client we keep
 * the button ENABLED during that window (label stays "Wait…", so we don't invite
 * smashing) and route each press to `onEarlyPress` — the parent counts them and locks
 * this player out locally once the smash limit is hit. `onEarlyPress` fires on
 * `pointerdown` (not click) so rapid, partial, or held taps all register. Once the
 * player is locked out or already holding the buzzer the button is inert again.
 *
 * `onLateRacePress` (#311, NFR-6.12 — late-race calibration grace): the SAME
 * disabled-button-swallows-the-press problem also biases the `BuzzAttempt` calibration
 * signal — once the authoritative "someone else won" `game_state` reaches this loser,
 * `canBuzz` flips false and the click is swallowed, so a press that lands after that
 * frame never reaches the server as a buzz and never journals as an attempt. During a
 * brief post-loss grace window the parent supplies this handler; each `pointerdown`
 * still fires the same `buzz` command (the server records the attempt as a loser via
 * its existing `!windowOpen` → `recordBuzzLoser` path — behavior-neutral, telemetry
 * only). The visible label stays "Wait…" (the disabled look) so there is no UX change.
 */

export interface BuzzerProps {
  canBuzz: boolean;
  active: boolean;
  lockedOut: boolean;
  onBuzz: () => void;
  /**
   * When provided, arms early-smash detection for the pre-open "Wait…" window: the
   * button stays tappable and each press calls this instead of buzzing. Omit it (the
   * default) to keep the Wait button inert — e.g. under the `ignore` policy, where an
   * early tap has no consequence to count.
   */
  onEarlyPress?: () => void;
  /**
   * When provided, keeps the button tappable during a short "lost-race" grace after
   * another player has won this clue — a `pointerdown` still fires this handler so the
   * press can journal as a `BuzzAttempt` loser for calibration (#311, NFR-6.12). The
   * visible label stays "Wait…" — no UX change; this only captures presses that a
   * disabled button would otherwise swallow. Mutually exclusive with `onEarlyPress` in
   * practice (they anchor to opposite phases of the clue: pre-open vs. post-award), but
   * if both are supplied the early-smash handler wins.
   */
  onLateRacePress?: () => void;
}

export function Buzzer({
  canBuzz,
  active,
  lockedOut,
  onBuzz,
  onEarlyPress,
  onLateRacePress,
}: BuzzerProps): React.JSX.Element {
  const label = active
    ? 'You buzzed!'
    : lockedOut
      ? 'Locked out'
      : canBuzz
        ? 'BUZZ'
        : 'Wait…';
  // The pre-open Wait window is "armed" when a smash handler is supplied and the button
  // isn't already live/locked/held. There we capture presses (pointerdown) instead of
  // buzzing — the disabled button would otherwise swallow the smash (FR-4.11).
  const earlyArmed = !canBuzz && !active && !lockedOut && onEarlyPress !== undefined;
  // #311: post-loss grace — capture late presses so the server can still journal the
  // BuzzAttempt (calibration only). Same "keep it tappable, but invisibly" trick as
  // earlyArmed. earlyArmed wins if both handlers are wired for the same render.
  const lateArmed =
    !canBuzz && !active && !lockedOut && !earlyArmed && onLateRacePress !== undefined;
  const tappable = canBuzz || earlyArmed || lateArmed;
  const cls =
    'buzzer' +
    (active ? ' buzzer--active' : lockedOut || !canBuzz ? ' buzzer--locked' : '');
  return (
    <button
      type="button"
      className={cls}
      disabled={!tappable}
      data-testid="buzzer"
      onClick={canBuzz ? onBuzz : undefined}
      onPointerDown={earlyArmed ? onEarlyPress : lateArmed ? onLateRacePress : undefined}
    >
      {label}
    </button>
  );
}
